import {
	type ColumnDef,
	type ColumnFiltersState,
	type SortingState,
	type VisibilityState,
	type PaginationState,
	type TableOptions,
	type RowSelectionState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	useReactTable,
} from "@tanstack/react-table";
import * as React from "react";

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { DataTablePagination } from "./data-table-pagination";

interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	toolbar?: (table: ReturnType<typeof useReactTable<TData>>) => React.ReactNode;
	pagination?: boolean;
	pageSize?: number;
	selectable?: boolean;
	onRowClick?: (row: TData) => void;
	className?: string;
	emptyMessage?: string;
	// Allow passing extra table options for full control
	tableOptions?: Partial<TableOptions<TData>>;
	initialColumnVisibility?: VisibilityState;
	initialColumnFilters?: ColumnFiltersState;
}

function DataTable<TData, TValue>({
	columns,
	data,
	toolbar,
	pagination = true,
	pageSize = 100,
	onRowClick,
	className,
	emptyMessage = "Aucun résultat.",
	tableOptions,
	initialColumnVisibility = {},
	initialColumnFilters = [],
}: DataTableProps<TData, TValue>) {
	const [sorting, setSorting] = React.useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialColumnFilters);
	const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(initialColumnVisibility);
	const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
	const [paginationState, setPaginationState] = React.useState<PaginationState>({
		pageIndex: 0,
		pageSize,
	});

	const table = useReactTable({
		data,
		columns,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection,
			pagination: paginationState,
		},
		enableMultiSort: true,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		onPaginationChange: setPaginationState,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
		getSortedRowModel: getSortedRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		...tableOptions,
	});

	return (
		<div className={cn("space-y-4", className)}>
			{toolbar?.(table)}
			<div className="rounded-md border">
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead key={header.id} colSpan={header.colSpan}>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && "selected"}
									className={onRowClick ? "cursor-pointer" : undefined}
									onClick={onRowClick ? () => onRowClick(row.original) : undefined}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="h-24 text-center"
								>
									{emptyMessage}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			{pagination && <DataTablePagination table={table} />}
		</div>
	);
}

export { DataTable, type DataTableProps };
